import { useEffect, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { appState } from "../../../state";
import { getDirectory } from "../../../mentions";
import { apiFetch } from "../../../api";
import { invitePartyApi } from "../../../partyApi";
import type { Track } from "../../hooks/useAudioPlayer";

// Same sole-recipient-auto-skip pattern as TogetherInviteDialog.tsx/
// SendGiftDialog.tsx. Unlike Watch Together, an initial track is optional -
// a room can start empty and fill up via the queue once both sides are in.
export function PartyInviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const recipients = getDirectory().filter((u) => u.email !== appState.currentUserEmail);
  const needsRecipientPicker = recipients.length > 1;
  const soleRecipient = recipients.length === 1 ? recipients[0] : null;

  const [recipientEmailChoice, setRecipientEmail] = useState("");
  const recipientEmail = recipientEmailChoice || soleRecipient?.email || "";
  const [tracks, setTracks] = useState<Track[]>([]);
  const [filename, setFilename] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setRecipientEmail("");
    setFilename("");
    (async () => {
      try {
        const res = await apiFetch("/api/music");
        if (!res.ok) return;
        const data = await res.json();
        setTracks(Array.isArray(data.tracks) ? data.tracks : []);
      } catch {
        setTracks([]);
      }
    })();
  }, [open]);

  function handleClose(): void {
    if (sending) return;
    onClose();
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError("");

    if (!recipientEmail) {
      setError("Choose who to invite.");
      return;
    }

    setSending(true);
    const result = await invitePartyApi({ recipientEmail, filename: filename || undefined });
    setSending(false);

    if (!result.ok) {
      setError(result.error || "Failed to send invite.");
      return;
    }

    onClose();
  }

  return (
    <AppDialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-music-note-beamed" title="Listen Together" onBack={handleClose} disabled={sending} />
      <DialogContent sx={{ pt: 1 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          {needsRecipientPicker && (
            <TextField
              select
              label="Invite"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              fullWidth
              size="small"
              disabled={sending}
            >
              {recipients.map((u) => (
                <MenuItem key={u.email} value={u.email}>
                  {u.fullName || u.email}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!recipients.length && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No one else to invite yet.
            </Alert>
          )}

          {!!tracks.length && (
            <TextField
              select
              label="Start with (optional)"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              fullWidth
              size="small"
              disabled={sending}
            >
              <MenuItem value="">Empty queue</MenuItem>
              {tracks.map((t) => (
                <MenuItem key={t.filename} value={t.filename}>
                  {t.title}
                </MenuItem>
              ))}
            </TextField>
          )}

          {error && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          <Button type="submit" variant="contained" color="primary" disabled={sending || !recipients.length} aria-label="Send invite">
            <i className="bi bi-music-note-beamed me-2" aria-hidden="true" style={{ marginRight: 8 }} />
            {sending ? "Inviting…" : "Invite to Listen"}
          </Button>
        </Box>
      </DialogContent>
    </AppDialog>
  );
}
